import { ThreeScene } from './ThreeScene';
import { UI } from './UI';
import { WebXRTracker } from './WebXRTracker';
import { AudioPlayer } from './AudioPlayer';
import { Utils } from './Utils';
import { XRFrame } from 'three';

export enum ARSupport {
  None,
  USDZ,
  WebXR,
}

export class Main {
  threeScene: ThreeScene | null;
  webXRTracker: WebXRTracker | null;
  audioPlayer: AudioPlayer | null;
  ui: UI | null;
  arSupport: ARSupport;
  customUSDZBanner: string;

  private audioReady: boolean = false;
  private sceneReady: boolean = false;

  constructor() {
    this.threeScene = null;
    this.webXRTracker = null;
    this.audioPlayer = null;
    this.ui = null;
    this.arSupport = ARSupport.None;
    this.customUSDZBanner = '#custom=' + location.href + 'static/banner.html';
    this.update = this.update.bind(this);
    this.checkARSupport();
  }

  private checkARSupport() {
    if (Utils.isIOS()) {
      let tempLinkElement = document.createElement('a');
      if (tempLinkElement.relList.supports('ar')) this.arSupport = ARSupport.USDZ;
      this.init();
    } else {
      if ('xr' in navigator) {
        let nav: any = navigator;
        nav.xr
          .isSessionSupported('immersive-ar')
          .then((supported: boolean) => {
            if (supported) this.arSupport = ARSupport.WebXR;
            this.init();
          })
          .catch((error: any) => {
            console.log('Error WebXR support: ' + error);
            this.init();
          });
      } else {
        console.log('WebXR is not supported: navigator.xr is undefined');
        this.init();
      }
    }
  }

  private init() {
    this.ui = new UI(this);
    this.ui.LinkClickedEvent.addEventListener(this.onLinkClicked.bind(this));
    this.ui.WebXRClickedEvent.addEventListener(this.onWebXRClicked.bind(this));

    if (this.arSupport == ARSupport.WebXR) {
      this.threeScene = new ThreeScene(this);
      this.threeScene.ModelLoadedEvent.addEventListener(this.onModelReady.bind(this));

      this.webXRTracker = new WebXRTracker(this);
      this.webXRTracker.LinkClickedEvent.addEventListener(this.onLinkClicked.bind(this));
      this.webXRTracker.ModelAnchoredEvent.addEventListener(this.onModelAnchored.bind(this));
      this.webXRTracker.SessionEndedEvent.addEventListener(this.onWebXRSessionEnded.bind(this));

      this.audioPlayer = new AudioPlayer();
      this.audioPlayer.AudioProgressEvent.addEventListener(this.onAudioReady.bind(this));

      let visProp = Utils.getHiddenProperty();
      if (visProp) {
        document.addEventListener(
          visProp.replace(/[H|h]idden/, '') + 'visibilitychange',
          this.onAndroidPageVisibilityChange.bind(this)
        );
      }
    } else if (this.arSupport == ARSupport.USDZ) {
      let visProp = Utils.getHiddenProperty();
      if (visProp) {
        document.addEventListener(
          visProp.replace(/[H|h]idden/, '') + 'visibilitychange',
          this.onIOSPageVisibilityChange.bind(this)
        );
      }
    }
  }

  private onAudioReady() {
    this.audioReady = true;
    this.checkSceneReady();
  }

  private onModelReady() {
    this.sceneReady = true;
    this.checkSceneReady();
  }

  private checkSceneReady() {
    if (this.audioReady && this.sceneReady) {
      if (this.threeScene) this.threeScene.renderer.setAnimationLoop(this.update.bind(this));
      if (this.webXRTracker) this.webXRTracker.sceneLoaded();
    }
  }

  private onLinkClicked() {
    window.open('https://lilnasx.lnk.to/FollowLilNasX', '_blank');
  }

  private onWebXRClicked() {
    document.documentElement.classList.add('loading-ar');
    this.webXRTracker?.start();
    this.threeScene?.load();
    this.audioPlayer?.catchInteraction();
  }

  private onWebXRSessionEnded() {
    this.audioPlayer?.stop('music');
    if (this.threeScene) this.threeScene.renderer.setAnimationLoop(null);
  }

  private onModelAnchored() {
    this.audioPlayer?.playAmbient('music');
  }

  private onIOSPageVisibilityChange() {
    if (Utils.isPageHidden()) {
      document.documentElement.classList.remove('loading-ar');
    }
  }

  private onAndroidPageVisibilityChange() {
    if (this.audioPlayer) {
      this.audioPlayer.setMuted(Utils.isPageHidden());
    }
  }

  update(_timestamp: number, frame?: XRFrame) {
    if (!frame) return;
    if (this.threeScene) this.threeScene.update();
    if (this.webXRTracker) this.webXRTracker.update(frame);
  }
}

new Main();
